<template>
  <div class="min-h-screen w-full flex items-center justify-center p-4" style="background: linear-gradient(135deg, #fef3c7 0%, #fefce8 50%, #ffffff 100%);">
    <div class="w-full max-w-[1200px] mx-auto">
      <section class="flex flex-col min-h-[500px] max-w-1000 w-full bg-white backdrop-blur-sm p-6 sm:p-8 md:p-10 rounded-2xl shadow-lg border border-yellow-100">
        <div class="flex flex-col flex-grow">
          <!-- Logo Section -->
          <div class="flex justify-center mb-6">
            <img src="@/assets/images/new-sidebar/icon.svg" alt="Company Logo" class="w-16 h-16" />
          </div>

          <!-- Header Section -->
          <div class="text-center">
            <h3 class="text-[32px] leading-tight font-semibold text-[#2c3e50]">
              We're excited to get started!
            </h3>
            <p class="text-[#666] mt-2">
              Let's Set Up Your Organization's Profile and begin your journey with us
            </p>

            <!-- Platform Values -->
            <div class="flex justify-center items-center gap-3 mt-5 mb-10">
              <div class="flex items-center gap-2">
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" fill="#f5bb00" />
                  <path d="M8 12L11 15L16 9" stroke="white" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
                <span class="text-[#666]">Capacity Planning</span>
              </div>
              <span class="text-[#666]">-</span>
              <div class="flex items-center gap-2">
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" fill="#f5bb00" />
                  <path d="M8 12L11 15L16 9" stroke="white" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
                <span class="text-[#666]">Capability Development</span>
              </div>
              <span class="text-[#666]">-</span>
              <div class="flex items-center gap-2">
                <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" fill="#f5bb00" />
                  <path d="M8 12L11 15L16 9" stroke="white" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
                <span class="text-[#666]">Career Succession</span>
              </div>
            </div>
          </div>

          <!-- Main Content Section -->
          <div class="flex-grow flex items-center justify-center px-2 sm:px-4 md:px-6">
            <div class="w-full max-w-600 mx-auto">
              <!-- Organization Name Section -->
              <div class="mt-8">
                <h4 class="text-[#2c3e50] font-medium mb-1 font-semibold">
                  Organization Name
                </h4>
                <p class="text-sm text-[#666] mb-3">This will be your unique identifier in our platform</p>
                <div class="w-full">
                  <input type="text" class="underlined-input w-full" v-model="orgName"
                    placeholder="e.g. Acme Corporation" />
                  <div v-if="orgName" class="flex items-center gap-2 mt-2">
                    <p class="text-sm text-[#666]">
                      Your Organization URL: <span class="font-medium text-[#2c3e50]">{{ slug }}</span>
                    </p>
                    <div v-if="slugLoading" class="loader"></div>
                    <check-icon v-else size="1x" class="text-[#44CC88]" />
                  </div>
                </div>
              </div>

              <!-- About Section -->
              <div class="mt-6">
                <h4 class="text-[#2c3e50] font-medium mb-1 font-semibold">
                  About Your Organization
                  <!-- <span class="text-sm font-normal text-[#666] ml-2">(Optional)</span> -->
                </h4>
                <p class="text-sm text-[#666] mb-3">Tell us about your mission and what makes your organization unique
                </p>
                <div class="w-full">
                  <textarea type="text" rows="3" class="underlined-input w-full resize-none" v-model="about"
                    placeholder="e.g. We're a technology company focused on making work life better..." />
                </div>
              </div>
            </div>
          </div>

          <!-- Footer Section -->
          <div class="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p class="text-sm text-[#666]">All your information is securely stored</p>
            <vs-button color="#f5bb00" class="w-full sm:w-auto px-8 py-2 rounded-full"
              :disabled="orgName.length < 3 || slugLoading" @click.stop="routeToNextStep">
              Continue
            </vs-button>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { CheckIcon } from "vue-feather-icons";
export default {
  data() {
    return {
      slug: "",
      orgName: "",
      about: "",
      slugLoading: false,
    };
  },
  components: {
    CheckIcon,
  },
  computed: {
    isUserLoggedIn() {
      return this.$store.state.auth.isUserLoggedIn;
    },
    userData() {
      return this.$store.state.users.data;
    },
    userId() {
      return this.$store.state.auth.user._id;
    },
    orgData() {
      return this.$store.state.organization.orgData;
    },
  },
  watch: {
    async orgName() {
      var slugAvailable = false;
      this.slugLoading = true;
      this.slug = this.orgName.replaceAll(" ", "_").toLowerCase();
      if (this.slug.length < 3) {
        return;
      }
      while (!slugAvailable) {
        slugAvailable = await this.checkSlug();
        if (!slugAvailable) {
          this.slug = `${this.slug}_${this.createSlugSuffix()}`;
        }
      }
      this.slugLoading = false;
    },
  },
  methods: {
    routeToNextStep() {
      const payload = {
        userId: this.userId,
        name: this.orgName,
        slug: this.slug,
        about: this.about,
      };

      this.$vs.loading();
      this.$store.dispatch("organization/createOrganization", payload).then((res) => {
        this.$vs.loading.close();
        this.$store.commit("onboarding/SET_ORGANIZATION", payload);
        this.$router.push(`/organization/${this.slug}/onboarding`);
      }).catch((err) => {
        this.$vs.loading.close();
        throw err;
      });
    },
    createSlugSuffix() {
      let result = "";
      const characters = "abcdefghijklmnopqrstuvwxyz0123456789";
      const charactersLength = characters.length;
      let counter = 0;
      while (counter < 5) {
        result += characters.charAt(
          Math.floor(Math.random() * charactersLength)
        );
        counter += 1;
      }
      return result;
    },
    async checkSlug() {
      try {
        const res = await this.$store.dispatch(
          "organization/checkSlug",
          this.slug
        );
        if (res.total > 0) {
          return false;
        } else {
          return true;
        }
      } catch (error) {
        return true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.max-w-600 {
  max-width: 600px !important;
}

.max-w-1000 {
  max-width: 1000px !important;
}

.loader {
  border: 2px solid #f3f3f3;
  border-top: 2px solid #3498db;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.underlined-input {
  border: none;
  border-bottom: 2px solid #d1d5db;
  padding: 8px 0;
  outline: none;
  transition: border-color 0.2s ease;
}

.ai-section {
  height: 15vh;
  cursor: pointer;
  background: linear-gradient(to right, #8b5cf6, #a855f7, #ec4899);
  padding: 2px;
  border-radius: 10px;
  transition-duration: 200ms;

  &:hover {
    transform: scale(1.03);
  }
}

.ai-content {
  height: 100%;
  background: white;
  border-radius: 8px;
  padding: 15px 10px;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.error-input {
  border: 1.5px solid red;
}

.underlined-input {
  font-size: 1rem;
  background-color: transparent;
  transition: all 0.2s ease;

  @media (min-width: 768px) {
    font-size: 1.125rem;
  }

  &:focus {
    border-color: #f5bb00;
    outline: none;
  }

  &::placeholder {
    color: #9ca3af;
  }
}
</style>
